import { ComponentRef, inject, runInInjectionContext } from '@angular/core';
import { outputToObservable } from '@angular/core/rxjs-interop';
import { IrisFilterPropertyMeta } from '@iris/modules/module-filters-v2/filter-meta';
import { defaultAppendQueryParamsFn } from '@iris/modules/module-filters-v2/query-params';
import { filterValueArrayIsEmpty } from '@iris/modules/module-filters-v2/utils';
import { StrakitSelectComponent } from '@ngx-strakit/cdk/controls';
import { Observable, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { IrisGlobalSandbox } from '@iris/common/redux/global.sandbox';

export function selectFilterMeta<TEntity>(
  propertyId: string,
  label: string,
  items: TEntity[],
  bindLabel: keyof TEntity,
  bindValue: keyof TEntity,
  isStrict: boolean,
  disableSearch?: boolean,
  dropdownWidthNative = true,
  appendBody = true,
): IrisFilterPropertyMeta {
  return {
    propertyId,
    label,
    appendFilterQueryParamsFn: defaultAppendQueryParamsFn<string>(
      propertyId,
      value => [isStrict ? value : `%${value}%`],
      t => t.strict(isStrict),
    ),
    filterRenderingOptions: {
      component: StrakitSelectComponent,
      getFilterValueFn: (componentInstance: StrakitSelectComponent): Observable<TEntity> => {
        return outputToObservable(componentInstance.changeEvent).pipe(
          switchMap(() => {
            return !componentInstance.selectedValues.length ? [undefined] : componentInstance.selectedValues.map((item) => item[bindValue]);
          }),
        );
      },
      initComponentFn: (componentRef: ComponentRef<StrakitSelectComponent>): Observable<void> => {
        componentRef.setInput('items', items);
        componentRef.setInput('searchable', !disableSearch);
        if (appendBody) { componentRef.setInput('appendTo', 'body'); }
        componentRef.setInput('bindLabel', String(bindLabel));
        componentRef.setInput('bindValue', String(bindValue));
        const componentElement = componentRef.location.nativeElement as HTMLElement;
        componentElement.classList.add('ng-select-inline');
        if (dropdownWidthNative) {
          componentElement.classList.add('dropdown-width-native');
        }

        const injector = componentRef.injector;

        runInInjectionContext(injector, () => {
          const globalSandbox = inject(IrisGlobalSandbox);
          const theme = globalSandbox.theme();

          if(theme?.strabagControlsEnabled) {
            componentRef.instance.stylingControlDirective.stylingMode.set('automatized');
            componentRef.instance.stylingControlDirective.noSubscriptBox.set(true);
            componentRef.instance.stylingControlDirective.extraDense.set(true);
          } else {
            componentRef.instance.stylingControlDirective.stylingMode.set('unformed');
          }
        });

        componentRef.instance.refreshItems();
        return of(null);
      },
      setValueFn: (componentInstance: StrakitSelectComponent, value: number | number[]): void => componentInstance.writeValue(value),
    },
    filterValueIsEmpty: filterValueArrayIsEmpty,
  };
}
